import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, apiBlob, logout, refreshSession } from './client.js';
import { useAuthStore } from '../auth/store.js';
import type {
  AdaptationOptions,
  Asset,
  AssetVersion,
  BrandSafetyReport,
  BrandSafetyRuleset,
  BrandSystem,
  ChannelSpec,
  SpecHealthReport,
  BroadcastOutcome,
  Campaign,
  CampaignExecution,
  ConnectorCatalogueEntry,
  WorkspacePublication,
  ConnectorConfig,
  PublishTarget,
  CanvaDesignPage,
  CanvaImportResult,
  AiGenerationResult,
  ConnectorHealth,
  CompatibilityReport,
  ConnectionCheckReport,
  ContentManifest,
  CopyGenerationResult,
  IngestBatchResult,
  DashboardResponse,
  SiteAnalyticsResponse,
  Destination,
  EstateOverview,
  Experiment,
  EngagementItem,
  EngagementReply,
  CompliancePack,
  SsoConnection,
  SsoConnectionInput,
  ScimTokenSummary,
  ScimTokenCreated,
  TrackedLink,
  ReferrerStat,
  CreateTrackedLinkInput,
  Report,
  ReportSnapshot,
  AssetTranslation,
  SavedView,
  WebhookSubscription,
  WebhookSubscriptionCreated,
  WebhookDelivery,
  CampaignDraft,
  ExperimentEvaluation,
  OptimisationResult,
  ExecutionSummary,
  NodeExecution,
  Organisation,
  OrganisationBranding,
  Page,
  Publication,
  RenderResult,
  RepurposePreview,
  Source,
  SyncSummary,
  Template,
  UsageResponse,
  WeeklyReviewResponse,
  Workspace,
} from './types.js';

/**
 * The DEDICATED hooks layer (§17.3, §21.4). Components consume these; they must
 * never call `api()` or `fetch` themselves.
 *
 * Query keys are hierarchical and workspace-prefixed so switching workspace
 * cannot serve another workspace's cached data — the client-side mirror of the
 * server's tenant isolation (§4.2).
 */

const keys = {
  workspaces: ['workspaces'] as const,
  organisations: ['organisations'] as const,
  organisation: (orgId: string) => ['organisation', orgId] as const,
  adminOverview: ['admin', 'overview'] as const,
  connectorCatalogue: ['connectors', 'catalogue'] as const,
  ws: (workspaceId: string) => ['ws', workspaceId] as const,
  assets: (workspaceId: string, filters?: string) =>
    ['ws', workspaceId, 'assets', filters ?? 'all'] as const,
  asset: (workspaceId: string, assetId: string) => ['ws', workspaceId, 'asset', assetId] as const,
  assetVersions: (workspaceId: string, assetId: string) =>
    ['ws', workspaceId, 'asset', assetId, 'versions'] as const,
  publications: (workspaceId: string, assetId: string) =>
    ['ws', workspaceId, 'asset', assetId, 'publications'] as const,
  connectors: (workspaceId: string) => ['ws', workspaceId, 'connectors'] as const,
  connectorHealth: (workspaceId: string) => ['ws', workspaceId, 'connectors', 'health'] as const,
  canvaDesigns: (workspaceId: string, configId: string, cursor?: string) =>
    ['ws', workspaceId, 'connectors', configId, 'canva-designs', cursor ?? ''] as const,
  publishTargets: (workspaceId: string, configId: string) =>
    ['ws', workspaceId, 'connectors', configId, 'publish-targets'] as const,
  sources: (workspaceId: string) => ['ws', workspaceId, 'sources'] as const,
  destinations: (workspaceId: string) => ['ws', workspaceId, 'destinations'] as const,
  campaigns: (workspaceId: string) => ['ws', workspaceId, 'campaigns'] as const,
  campaign: (workspaceId: string, id: string) => ['ws', workspaceId, 'campaign', id] as const,
  executions: (workspaceId: string, id: string) =>
    ['ws', workspaceId, 'campaign', id, 'executions'] as const,
  execution: (workspaceId: string, id: string, execId: string) =>
    ['ws', workspaceId, 'campaign', id, 'execution', execId] as const,
  brandSafetyRules: (workspaceId: string) => ['ws', workspaceId, 'brand-safety-rules'] as const,
  compliancePack: (workspaceId: string) => ['ws', workspaceId, 'compliance-pack'] as const,
  channelSpecs: (workspaceId: string) => ['ws', workspaceId, 'channel-specs'] as const,
  channelSpecHealth: (workspaceId: string) =>
    ['ws', workspaceId, 'channel-specs', 'health'] as const,
  adaptationOptions: (workspaceId: string) => ['ws', workspaceId, 'adaptation-options'] as const,
  experiments: (workspaceId: string) => ['ws', workspaceId, 'experiments'] as const,
  experiment: (workspaceId: string, id: string) => ['ws', workspaceId, 'experiment', id] as const,
  brandSystem: (workspaceId: string) => ['ws', workspaceId, 'brand-system'] as const,
  templates: (workspaceId: string) => ['ws', workspaceId, 'templates'] as const,
  dashboard: (workspaceId: string, scope: string, id: string) =>
    ['ws', workspaceId, 'dashboard', scope, id] as const,
  weeklyReview: (workspaceId: string) => ['ws', workspaceId, 'weekly-review'] as const,
  siteAnalytics: (workspaceId: string) => ['ws', workspaceId, 'site-analytics'] as const,
  usage: (workspaceId: string) => ['ws', workspaceId, 'usage'] as const,
  engagements: (workspaceId: string) => ['ws', workspaceId, 'engagements'] as const,
  engagementReplies: (workspaceId: string, itemId: string) =>
    ['ws', workspaceId, 'engagement', itemId, 'replies'] as const,
  assetPreview: (workspaceId: string, assetId: string) =>
    ['ws', workspaceId, 'asset', assetId, 'preview'] as const,
  sso: (orgId: string) => ['organisation', orgId, 'sso'] as const,
  scimTokens: (orgId: string) => ['organisation', orgId, 'scim-tokens'] as const,
  links: (workspaceId: string) => ['ws', workspaceId, 'links'] as const,
  reports: (workspaceId: string) => ['ws', workspaceId, 'reports'] as const,
  sharedReport: (token: string) => ['shared-report', token] as const,
  translations: (workspaceId: string, assetId: string) =>
    ['ws', workspaceId, 'asset', assetId, 'translations'] as const,
  webhooks: (workspaceId: string) => ['ws', workspaceId, 'webhooks'] as const,
  savedViews: (workspaceId: string, surface: string) =>
    ['ws', workspaceId, 'saved-views', surface] as const,
  webhookDeliveries: (workspaceId: string, id: string) =>
    ['ws', workspaceId, 'webhook', id, 'deliveries'] as const,
};

export { keys as queryKeys };

/**
 * Is this failure a "the thing does not exist yet" (§13.3)? Screens may not
 * import the raw API client, so the ApiError shape is interpreted here — a
 * missing Brand System is a normal empty state, not a broken screen.
 */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

// --- Tenancy ---

export function useWorkspaces() {
  return useQuery({
    queryKey: keys.workspaces,
    queryFn: () => api<Page<Workspace>>('/workspaces'),
  });
}

/** Estate-wide overview for platform admins (§6). The API rejects non-admins. */
export function useAdminOverview() {
  return useQuery({
    queryKey: keys.adminOverview,
    queryFn: () => api<EstateOverview>('/admin/overview'),
  });
}

export function useOrganisations() {
  return useQuery({
    queryKey: keys.organisations,
    queryFn: () =>
      api<{ data: { organisationId: string; organisationName: string; orgRole: string }[] }>(
        '/organisations',
      ),
  });
}

/**
 * A single organisation, including its white-label branding (§20.5). The caller
 * may only read its own org; the API enforces that from the token's org claim.
 */
export function useOrganisation(orgId: string) {
  return useQuery({
    queryKey: keys.organisation(orgId),
    queryFn: () => api<Organisation>(`/organisations/${orgId}`),
    enabled: orgId !== '',
  });
}

/**
 * Replace an organisation's white-label branding (§20.5). Sends the full record
 * — the server treats a PATCH as a whole-config replace, with null meaning "back
 * to the estate default". Invalidates the org so the shell re-resolves its brand.
 */
export function useUpdateOrganisationBranding(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (branding: OrganisationBranding) =>
      api<Organisation>(`/organisations/${orgId}`, { method: 'PATCH', body: { branding } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.organisation(orgId) });
    },
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; slug: string }) =>
      api<Workspace>('/workspaces', { method: 'POST', body: input }),
    onSuccess: async () => {
      // Creating a workspace grants the creator an owner role server-side, but
      // the current access token predates it. Refresh so that grant is in the
      // token immediately, instead of the operator hitting "no permission" in
      // the new workspace until they next sign in.
      await refreshSession();
      await qc.invalidateQueries({ queryKey: keys.workspaces });
    },
  });
}

/**
 * Sign out: revoke the session server-side, then clear the in-memory auth store
 * so the route guards return the user to the front door. The refresh cookie is
 * httpOnly, so only the server can expire it — this hook is the screen's one way
 * to end a session without touching `fetch` or the raw client itself (§21.4).
 */
export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  return useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      clear();
    },
  });
}

// --- Assets ---

export function useAssets(
  workspaceId: string,
  filters: { type?: string; state?: string; includeArchived?: boolean } = {},
) {
  const query = new URLSearchParams();
  if (filters.type !== undefined) query.set('type', filters.type);
  if (filters.state !== undefined) query.set('state', filters.state);
  if (filters.includeArchived !== undefined)
    query.set('includeArchived', String(filters.includeArchived));
  const suffix = query.toString();
  return useQuery({
    queryKey: keys.assets(workspaceId, suffix),
    queryFn: () =>
      api<Page<Asset>>(`/workspaces/${workspaceId}/assets${suffix === '' ? '' : `?${suffix}`}`),
    enabled: workspaceId !== '',
  });
}

/** The workspace publish feed — every published post with its content and channel. */
export function useWorkspacePublications(workspaceId: string) {
  return useQuery({
    queryKey: ['workspace-publications', workspaceId],
    queryFn: () => api<{ data: WorkspacePublication[] }>(`/workspaces/${workspaceId}/publications`),
    enabled: workspaceId !== '',
  });
}

export function useAsset(workspaceId: string, assetId: string) {
  return useQuery({
    queryKey: keys.asset(workspaceId, assetId),
    queryFn: () => api<Asset>(`/workspaces/${workspaceId}/assets/${assetId}`),
    enabled: workspaceId !== '' && assetId !== '',
  });
}

export function useAssetVersions(workspaceId: string, assetId: string) {
  return useQuery({
    queryKey: keys.assetVersions(workspaceId, assetId),
    queryFn: () =>
      api<{ data: AssetVersion[] }>(`/workspaces/${workspaceId}/assets/${assetId}/versions`),
    enabled: workspaceId !== '' && assetId !== '',
  });
}

export function useCreateAsset(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { type: string; data: Record<string, unknown>; visibility: string }) =>
      api<Asset>(`/workspaces/${workspaceId}/assets`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'assets'] }),
  });
}

/**
 * AI Ingress (ai-ingress §4A): POST a Content Manifest to create DRAFT assets,
 * one per post. Available on every plan; the only gate is the asset quota. The
 * asset list is invalidated on success so the new drafts appear immediately.
 * Nothing is published — imported content lands as drafts for human review.
 */
export function useIngestManifest(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (manifest: ContentManifest) =>
      api<IngestBatchResult>(`/workspaces/${workspaceId}/ingest`, {
        method: 'POST',
        body: manifest,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'assets'] }),
  });
}

/** Lifecycle transition. Delisting cascades removal server-side (§2.3). */
export function useChangeAssetState(workspaceId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (state: string) =>
      api<Asset>(`/workspaces/${workspaceId}/assets/${assetId}/state`, {
        method: 'POST',
        body: { state },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.asset(workspaceId, assetId) });
      void qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'assets'] });
      void qc.invalidateQueries({ queryKey: keys.assetVersions(workspaceId, assetId) });
    },
  });
}

export function usePublications(workspaceId: string, assetId: string) {
  return useQuery({
    queryKey: keys.publications(workspaceId, assetId),
    queryFn: () =>
      api<Page<Publication>>(`/workspaces/${workspaceId}/assets/${assetId}/publications`),
    enabled: workspaceId !== '' && assetId !== '',
  });
}

export function usePublishAsset(workspaceId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (destinationId: string) =>
      api<{ verified: boolean; publication: Publication }>(
        `/workspaces/${workspaceId}/assets/${assetId}/publish`,
        { method: 'POST', body: { destinationId } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.publications(workspaceId, assetId) }),
  });
}

/** Brand-safety pre-flight for an asset before it goes out (§21). Read-only. */
export function useBrandSafetyCheck(workspaceId: string, assetId: string) {
  return useMutation({
    mutationFn: () =>
      api<BrandSafetyReport>(`/workspaces/${workspaceId}/assets/${assetId}/brand-safety-check`, {
        method: 'POST',
        body: {},
      }),
  });
}

/**
 * Preview how this hero Asset auto-adapts to each connected channel (§9): text
 * fit to per-channel limits and the media format each channel prefers. Read-only.
 */
export function useRepurposePlan(workspaceId: string, assetId: string) {
  return useMutation({
    mutationFn: (destinationIds?: string[]) =>
      api<RepurposePreview>(`/workspaces/${workspaceId}/assets/${assetId}/repurpose-plan`, {
        method: 'POST',
        body: destinationIds !== undefined ? { destinationIds } : {},
      }),
  });
}

/**
 * Pre-broadcast compatibility & fit report (§6.4, §9): what would land where, and
 * why any channel is skipped — without publishing. Read-only.
 */
export function useCompatibilityReport(workspaceId: string, assetId: string) {
  return useMutation({
    mutationFn: (destinationIds?: string[]) =>
      api<CompatibilityReport>(`/workspaces/${workspaceId}/assets/${assetId}/compatibility`, {
        method: 'POST',
        body: destinationIds !== undefined ? { destinationIds } : {},
      }),
  });
}

/**
 * Broadcast: publish this Asset to every connected channel at once (§2.3). With
 * no argument it targets every active channel; pass a subset of destination ids
 * to target some. Returns the per-channel outcome.
 */
export function useBroadcastAsset(workspaceId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (destinationIds?: string[]) =>
      api<BroadcastOutcome>(`/workspaces/${workspaceId}/assets/${assetId}/broadcast`, {
        method: 'POST',
        body: destinationIds !== undefined ? { destinationIds } : {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.publications(workspaceId, assetId) }),
  });
}

/**
 * Generate AI-written, audience-tailored copy for this Asset, one variant per
 * connected channel (§9, §21). Pass an optional free-text audience segment to tune
 * the voice. Output is disclosed as AI-authored and never published automatically —
 * a person reviews it first. Read-only with respect to the asset.
 */
export function useGenerateCopy(workspaceId: string, assetId: string) {
  return useMutation({
    mutationFn: (input: { audience?: string; destinationIds?: string[] } = {}) =>
      api<CopyGenerationResult>(`/workspaces/${workspaceId}/assets/${assetId}/generate-copy`, {
        method: 'POST',
        body: {
          ...(input.audience !== undefined ? { audience: input.audience } : {}),
          ...(input.destinationIds !== undefined ? { destinationIds: input.destinationIds } : {}),
        },
      }),
  });
}

// --- Adaptation Engine options (§9) ---

/** Read the workspace's Adaptation Engine options (e.g. branded overlay). */
export function useAdaptationOptions(workspaceId: string) {
  return useQuery({
    queryKey: keys.adaptationOptions(workspaceId),
    queryFn: () => api<AdaptationOptions>(`/workspaces/${workspaceId}/adaptation-options`),
    enabled: workspaceId !== '',
  });
}

/** Set the workspace's Adaptation Engine options. */
export function useUpdateAdaptationOptions(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdaptationOptions) =>
      api<AdaptationOptions>(`/workspaces/${workspaceId}/adaptation-options`, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.adaptationOptions(workspaceId) }),
  });
}

// --- Channel Spec Library: the gold standard per channel (§6.4, §9) ---

/** The authoritative, sourced, versioned gold standard for every channel. */
export function useChannelSpecs(workspaceId: string) {
  return useQuery({
    queryKey: keys.channelSpecs(workspaceId),
    queryFn: () => api<{ data: ChannelSpec[] }>(`/workspaces/${workspaceId}/channel-specs`),
    enabled: workspaceId !== '',
  });
}

/** Spec drift health: freshness + connector-vs-registry consistency per channel (§9 Phase 5). */
export function useChannelSpecHealth(workspaceId: string) {
  return useQuery({
    queryKey: keys.channelSpecHealth(workspaceId),
    queryFn: () => api<SpecHealthReport>(`/workspaces/${workspaceId}/channel-specs/health`),
    enabled: workspaceId !== '',
  });
}

// --- HIVE optimisation intelligence (§10.3) ---

/**
 * Ask HIVE for optimisation recommendations for this workspace. Advisory AI
 * intelligence, provenance-tagged and disclosed; returns a clear "not configured"
 * error until HIVE is wired. A mutation because it triggers an external call.
 */
export function useOptimisation(workspaceId: string) {
  return useMutation({
    mutationFn: () =>
      api<OptimisationResult>(`/workspaces/${workspaceId}/optimisation`, {
        method: 'POST',
        body: {},
      }),
  });
}

// --- Brand-safety rules (§21) ---

/** Read the workspace's brand-safety ruleset (prohibited + review terms). */
export function useBrandSafetyRules(workspaceId: string) {
  return useQuery({
    queryKey: keys.brandSafetyRules(workspaceId),
    queryFn: () => api<BrandSafetyRuleset>(`/workspaces/${workspaceId}/brand-safety-rules`),
    enabled: workspaceId !== '',
  });
}

/** Save the workspace's brand-safety ruleset; prohibited terms hard-block a publish. */
export function useUpdateBrandSafetyRules(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BrandSafetyRuleset) =>
      api<BrandSafetyRuleset>(`/workspaces/${workspaceId}/brand-safety-rules`, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.brandSafetyRules(workspaceId) }),
  });
}

/** The workspace's compliance pack (§21): required disclosures + prohibited claims. */
export function useCompliancePack(workspaceId: string) {
  return useQuery({
    queryKey: keys.compliancePack(workspaceId),
    queryFn: () => api<CompliancePack>(`/workspaces/${workspaceId}/compliance-pack`),
    enabled: workspaceId !== '',
  });
}

/** Save the workspace's compliance pack; a missing required disclosure blocks (or is appended). */
export function useUpdateCompliancePack(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompliancePack) =>
      api<CompliancePack>(`/workspaces/${workspaceId}/compliance-pack`, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.compliancePack(workspaceId) }),
  });
}

// --- Enterprise auth: SAML SSO + SCIM provisioning (§14.5) ---

/** The organisation's SAML SSO connection, or null if not yet configured. */
export function useSsoConnection(orgId: string) {
  return useQuery({
    queryKey: keys.sso(orgId),
    queryFn: () => api<{ data: SsoConnection | null }>(`/organisations/${orgId}/sso`),
    enabled: orgId !== '',
  });
}

/** Create or replace the organisation's SSO connection (dormant unless enabled). */
export function useUpsertSsoConnection(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SsoConnectionInput) =>
      api<{ data: SsoConnection }>(`/organisations/${orgId}/sso`, { method: 'PUT', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.sso(orgId) }),
  });
}

/** The organisation's SCIM provisioning tokens (metadata only). */
export function useScimTokens(orgId: string) {
  return useQuery({
    queryKey: keys.scimTokens(orgId),
    queryFn: () => api<{ data: ScimTokenSummary[] }>(`/organisations/${orgId}/scim-tokens`),
    enabled: orgId !== '',
  });
}

/** Mint a SCIM token; the response carries the plaintext exactly once. */
export function useCreateScimToken(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      api<{ data: ScimTokenCreated }>(`/organisations/${orgId}/scim-tokens`, {
        method: 'POST',
        body: { label },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.scimTokens(orgId) }),
  });
}

/** Revoke a SCIM token. */
export function useRevokeScimToken(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      api<void>(`/organisations/${orgId}/scim-tokens/${tokenId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.scimTokens(orgId) }),
  });
}

// --- Attribution & UTM (§20.5) ---

/** Tracked links with click counts, plus the workspace referrer breakdown. */
export function useTrackedLinks(workspaceId: string) {
  return useQuery({
    queryKey: keys.links(workspaceId),
    queryFn: () =>
      api<{ data: TrackedLink[]; referrers: ReferrerStat[] }>(`/workspaces/${workspaceId}/links`),
    enabled: workspaceId !== '',
  });
}

/** Create a tracked link; the response carries the public short URL to share. */
export function useCreateTrackedLink(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTrackedLinkInput) =>
      api<{ data: TrackedLink }>(`/workspaces/${workspaceId}/links`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.links(workspaceId) }),
  });
}

/** Delete a tracked link. */
export function useDeleteTrackedLink(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ deleted: boolean }>(`/workspaces/${workspaceId}/links/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.links(workspaceId) }),
  });
}

// --- Client reporting + portals (§20.5) ---

export function useReports(workspaceId: string) {
  return useQuery({
    queryKey: keys.reports(workspaceId),
    queryFn: () => api<{ data: Report[] }>(`/workspaces/${workspaceId}/reports`),
    enabled: workspaceId !== '',
  });
}

export function useGenerateReport(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; from?: string; to?: string }) =>
      api<{ data: Report }>(`/workspaces/${workspaceId}/reports`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.reports(workspaceId) }),
  });
}

/** Open a share portal; the response carries the one-time token for `<spa>/r/<token>`. */
export function useShareReport(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) =>
      api<{ data: { token: string } }>(`/workspaces/${workspaceId}/reports/${reportId}/share`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.reports(workspaceId) }),
  });
}

export function useUnshareReport(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) =>
      api<{ unshared: boolean }>(`/workspaces/${workspaceId}/reports/${reportId}/share`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.reports(workspaceId) }),
  });
}

export function useDeleteReport(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) =>
      api<{ deleted: boolean }>(`/workspaces/${workspaceId}/reports/${reportId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.reports(workspaceId) }),
  });
}

/** The public portal read — resolves a shared report by token, no session needed. */
export function useSharedReport(token: string) {
  return useQuery({
    queryKey: keys.sharedReport(token),
    queryFn: () =>
      api<{ data: { title: string; snapshot: ReportSnapshot } }>(`/reports/shared/${token}`),
    enabled: token !== '',
    retry: false,
  });
}

// --- AI campaign assistant (§20.5) ---

export function useDraftCampaign(workspaceId: string) {
  return useMutation({
    mutationFn: (input: { goal: string; audience: string | null; channels: string[] }) =>
      api<{ data: CampaignDraft }>(`/workspaces/${workspaceId}/ai/campaign-draft`, {
        method: 'POST',
        body: input,
      }),
  });
}

export function useApplyCampaignDraft(workspaceId: string) {
  return useMutation({
    mutationFn: (input: { name: string; heroTitle: string; heroBody: string }) =>
      api<{ data: { assetId: string } }>(`/workspaces/${workspaceId}/ai/campaign-apply`, {
        method: 'POST',
        body: input,
      }),
  });
}

// --- Public API + webhooks (§20.5) ---

export function useWebhooks(workspaceId: string) {
  return useQuery({
    queryKey: keys.webhooks(workspaceId),
    queryFn: () => api<{ data: WebhookSubscription[] }>(`/workspaces/${workspaceId}/webhooks`),
    enabled: workspaceId !== '',
  });
}

export function useCreateWebhook(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { url: string; events: string[]; description: string | null }) =>
      api<{ data: WebhookSubscriptionCreated }>(`/workspaces/${workspaceId}/webhooks`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.webhooks(workspaceId) }),
  });
}

export function useToggleWebhook(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      api<{ updated: boolean }>(`/workspaces/${workspaceId}/webhooks/${input.id}/active`, {
        method: 'POST',
        body: { active: input.active },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.webhooks(workspaceId) }),
  });
}

export function usePingWebhook(workspaceId: string) {
  return useMutation({
    mutationFn: (id: string) =>
      api<{ delivered: boolean }>(`/workspaces/${workspaceId}/webhooks/${id}/ping`, {
        method: 'POST',
      }),
  });
}

export function useDeleteWebhook(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ deleted: boolean }>(`/workspaces/${workspaceId}/webhooks/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.webhooks(workspaceId) }),
  });
}

/** A user's saved views for a data surface (§17.2). */
export function useSavedViews(workspaceId: string, surface: string) {
  return useQuery({
    queryKey: keys.savedViews(workspaceId, surface),
    queryFn: () =>
      api<{ data: SavedView[] }>(
        `/workspaces/${workspaceId}/saved-views?surface=${encodeURIComponent(surface)}`,
      ),
    enabled: workspaceId !== '' && surface !== '',
  });
}

export function useSaveView(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { surface: string; name: string; config: Record<string, unknown> }) =>
      api<{ data: SavedView }>(`/workspaces/${workspaceId}/saved-views`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'saved-views'] }),
  });
}

export function useDeleteSavedView(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ removed: boolean }>(`/workspaces/${workspaceId}/saved-views/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'saved-views'] }),
  });
}

export function useWebhookDeliveries(workspaceId: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.webhookDeliveries(workspaceId, id),
    queryFn: () =>
      api<{ data: WebhookDelivery[] }>(`/workspaces/${workspaceId}/webhooks/${id}/deliveries`),
    enabled: enabled && workspaceId !== '' && id !== '',
  });
}

// --- Localization (§20.5) ---

export function useAssetTranslations(workspaceId: string, assetId: string) {
  return useQuery({
    queryKey: keys.translations(workspaceId, assetId),
    queryFn: () =>
      api<{ data: AssetTranslation[] }>(
        `/workspaces/${workspaceId}/assets/${assetId}/translations`,
      ),
    enabled: workspaceId !== '' && assetId !== '',
  });
}

export function useSetTranslation(workspaceId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { locale: string; fields: Record<string, string> }) =>
      api<{ data: AssetTranslation }>(
        `/workspaces/${workspaceId}/assets/${assetId}/translations/${input.locale}`,
        { method: 'PUT', body: { fields: input.fields } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.translations(workspaceId, assetId) }),
  });
}

export function useRemoveTranslation(workspaceId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locale: string) =>
      api<{ removed: boolean }>(
        `/workspaces/${workspaceId}/assets/${assetId}/translations/${locale}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.translations(workspaceId, assetId) }),
  });
}

// --- A/B experimentation with auto-promote (§20.5) ---

export function useExperiments(workspaceId: string) {
  return useQuery({
    queryKey: keys.experiments(workspaceId),
    queryFn: () => api<{ data: Experiment[] }>(`/workspaces/${workspaceId}/experiments`),
    enabled: workspaceId !== '',
  });
}

export function useCreateExperiment(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      destinationId: string;
      metric: string;
      variants: { assetId: string; label: string }[];
    }) =>
      api<Experiment>(`/workspaces/${workspaceId}/experiments`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.experiments(workspaceId) }),
  });
}

/** Start an experiment: publishes every variant to the slot. */
export function useStartExperiment(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (experimentId: string) =>
      api<Experiment>(`/workspaces/${workspaceId}/experiments/${experimentId}/start`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.experiments(workspaceId) }),
  });
}

/**
 * Evaluate an experiment: reads measured standings and auto-promotes the winner
 * when the evidence is clear, or reports why it is holding (never invents one).
 */
export function useEvaluateExperiment(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (experimentId: string) =>
      api<ExperimentEvaluation>(`/workspaces/${workspaceId}/experiments/${experimentId}/evaluate`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.experiments(workspaceId) }),
  });
}

// --- Engagement (§20.6): the inbox + governed replies ---

/** The inbox — inbound engagement for the workspace. */
export function useEngagements(workspaceId: string, status?: 'open' | 'replied' | 'dismissed') {
  return useQuery({
    queryKey: keys.engagements(workspaceId),
    queryFn: () =>
      api<{ data: EngagementItem[] }>(
        `/workspaces/${workspaceId}/engagement${status !== undefined ? `?status=${status}` : ''}`,
      ),
    enabled: workspaceId !== '',
  });
}

/** Pull fresh inbound engagement from a connected channel. */
export function useFetchEngagement(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectorConfigId: string; since?: string }) =>
      api<{ fetched: number }>(`/workspaces/${workspaceId}/engagement/fetch`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.engagements(workspaceId) }),
  });
}

/** Propose a reply to an inbound item — records intent, sends nothing yet. */
export function useProposeReply(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { itemId: string; draftText: string }) =>
      api<{ reply: EngagementReply }>(
        `/workspaces/${workspaceId}/engagement/${input.itemId}/replies`,
        { method: 'POST', body: { draftText: input.draftText } },
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: keys.engagements(workspaceId) });
      void qc.invalidateQueries({ queryKey: keys.engagementReplies(workspaceId, input.itemId) });
    },
  });
}

/** Approve a pending reply — the human gate; only now does the brand speak. */
export function useApproveReply(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (replyId: string) =>
      api<{ reply: EngagementReply }>(
        `/workspaces/${workspaceId}/engagement-replies/${replyId}/approve`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.engagements(workspaceId) }),
  });
}

/** Reject a pending reply — records the decision; the channel is untouched. */
export function useRejectReply(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { replyId: string; reason: string }) =>
      api<{ reply: EngagementReply }>(
        `/workspaces/${workspaceId}/engagement-replies/${input.replyId}/reject`,
        { method: 'POST', body: { reason: input.reason } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.engagements(workspaceId) }),
  });
}

/** The reply ledger for one item — the governance history. */
export function useEngagementReplies(workspaceId: string, itemId: string) {
  return useQuery({
    queryKey: keys.engagementReplies(workspaceId, itemId),
    queryFn: () =>
      api<{ data: EngagementReply[] }>(`/workspaces/${workspaceId}/engagement/${itemId}/replies`),
    enabled: workspaceId !== '' && itemId !== '',
  });
}

// --- Connectors, sources, destinations ---

export function useConnectorCatalogue() {
  return useQuery({
    queryKey: keys.connectorCatalogue,
    queryFn: () => api<{ data: ConnectorCatalogueEntry[] }>('/connectors'),
  });
}

export function useConnectorConfigs(workspaceId: string) {
  return useQuery({
    queryKey: keys.connectors(workspaceId),
    queryFn: () => api<Page<ConnectorConfig>>(`/workspaces/${workspaceId}/connectors`),
    enabled: workspaceId !== '',
  });
}

export function useConnectorHealth(workspaceId: string) {
  return useQuery({
    queryKey: keys.connectorHealth(workspaceId),
    queryFn: () => api<{ data: ConnectorHealth[] }>(`/workspaces/${workspaceId}/connectors/health`),
    enabled: workspaceId !== '',
    // Health is live state; refresh it while the page is open.
    refetchInterval: 30_000,
  });
}

export function useCreateConnectorConfig(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      connectorKey: string;
      displayName: string;
      credentials: Record<string, string>;
      settings: Record<string, unknown>;
    }) =>
      api<ConnectorConfig>(`/workspaces/${workspaceId}/connectors`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.connectors(workspaceId) }),
  });
}

/**
 * Begin the one-click "Connect with <Platform>" flow. Returns the provider
 * authorize URL; the caller sends the browser there. No cache to invalidate — the
 * new connector lands when the provider redirects back to the callback.
 */
export function useStartOAuth(workspaceId: string) {
  return useMutation({
    mutationFn: (input: { connectorKey: string; displayName: string }) =>
      api<{ authorizeUrl: string }>(
        `/workspaces/${workspaceId}/connectors/oauth/${input.connectorKey}/start`,
        { method: 'POST', body: { displayName: input.displayName } },
      ),
  });
}

/**
 * Validate a connector's stored credentials against the real provider (§7.4).
 *
 * Read-only by construction on the server: health, quota and one page of
 * resources. Safe to run against a live client account — nothing is created,
 * modified or removed there.
 */
export function useConnectionCheck(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectorConfigId: string) =>
      api<{ data: ConnectionCheckReport }>(
        `/workspaces/${workspaceId}/connectors/${connectorConfigId}/connection-check`,
        { method: 'POST' },
      ),
    // A check tells us what health currently is, so the health table is stale.
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.connectorHealth(workspaceId) }),
  });
}

/**
 * List the destinations a "Lane B" connection can publish to — Pages, boards,
 * Merchant accounts (§7.1). Enabled only when caller passes a config id, so the
 * picker fetches lazily when opened. Read-only on the server.
 */
export function usePublishTargets(workspaceId: string, connectorConfigId: string) {
  return useQuery({
    queryKey: keys.publishTargets(workspaceId, connectorConfigId),
    queryFn: () =>
      api<{ data: PublishTarget[] }>(
        `/workspaces/${workspaceId}/connectors/${connectorConfigId}/publish-targets`,
      ),
    enabled: workspaceId !== '' && connectorConfigId !== '',
    // The account's Pages/boards rarely change within a session; don't refetch on
    // focus, but don't cache stale either.
    staleTime: 60_000,
    retry: false,
  });
}

/** Record the chosen destination; refreshes the connector list so the new owner shows. */
export function useSetPublishTarget(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectorConfigId: string; applySettings: Record<string, string> }) =>
      api<{ data: ConnectorConfig }>(
        `/workspaces/${workspaceId}/connectors/${input.connectorConfigId}/publish-target`,
        { method: 'POST', body: { applySettings: input.applySettings } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.connectors(workspaceId) }),
  });
}

/**
 * List the designs a connected Canva account can import (creative source, §7.2).
 * Enabled only once a Canva connection is chosen, so the picker fetches lazily
 * when opened. Read-only on the server — nothing is created in Canva or INTEGRA.
 */
export function useCanvaDesigns(workspaceId: string, connectorConfigId: string, cursor?: string) {
  return useQuery({
    queryKey: keys.canvaDesigns(workspaceId, connectorConfigId, cursor),
    queryFn: () =>
      api<{ data: CanvaDesignPage }>(
        `/workspaces/${workspaceId}/connectors/${connectorConfigId}/canva/designs${
          cursor !== undefined && cursor !== '' ? `?cursor=${encodeURIComponent(cursor)}` : ''
        }`,
      ),
    enabled: workspaceId !== '' && connectorConfigId !== '',
    // A Canva account's designs rarely change within a session; don't refetch on
    // focus, but don't cache stale either.
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Import ONE Canva design as a high-resolution image Asset (creative source,
 * Phase 1). The export is re-hosted server-side as the org's master and lands as
 * a draft; the asset list is invalidated so it appears immediately. Nothing is
 * published — the imported design is a draft for review and repurposing.
 */
export function useImportFromCanva(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectorConfigId: string; designId: string }) =>
      api<{ data: CanvaImportResult }>(
        `/workspaces/${workspaceId}/connectors/${input.connectorConfigId}/canva/import`,
        { method: 'POST', body: { designId: input.designId } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'assets'] }),
  });
}

/**
 * Generate a NEW image from a prompt with AI (creative source, Phase 2). The
 * result is re-hosted server-side as the org's master and lands as a draft image
 * Asset; the asset list is invalidated so it appears immediately. Dormant (a
 * clear error) until a provider is configured on the deployment. Nothing is
 * published — the generated image is a draft for review and repurposing.
 */
export function useGenerateImage(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prompt: string; provider?: string; size?: string }) =>
      api<{ data: AiGenerationResult }>(`/workspaces/${workspaceId}/generate-image`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'assets'] }),
  });
}

export function useSources(workspaceId: string) {
  return useQuery({
    queryKey: keys.sources(workspaceId),
    queryFn: () => api<Page<Source>>(`/workspaces/${workspaceId}/sources`),
    enabled: workspaceId !== '',
  });
}

export function useCreateSource(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      connectorConfigId: string;
      schemaMapping: { fieldMap: Record<string, string>; requiredFields: string[] };
      syncConfig: { mode: string; cadenceCron: string | null; conflictResolution: string };
    }) => api<Source>(`/workspaces/${workspaceId}/sources`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.sources(workspaceId) }),
  });
}

export function useRunSync(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) =>
      api<SyncSummary>(`/workspaces/${workspaceId}/sources/${sourceId}/sync`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.sources(workspaceId) });
      void qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'assets'] });
    },
  });
}

export function useDestinations(workspaceId: string) {
  return useQuery({
    queryKey: keys.destinations(workspaceId),
    queryFn: () => api<Page<Destination>>(`/workspaces/${workspaceId}/destinations`),
    enabled: workspaceId !== '',
  });
}

export function useCreateDestination(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectorConfigId: string }) =>
      api<Destination>(`/workspaces/${workspaceId}/destinations`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.destinations(workspaceId) }),
  });
}

// --- Campaigns (§20.4) ---

export function useCampaigns(workspaceId: string) {
  return useQuery({
    queryKey: keys.campaigns(workspaceId),
    queryFn: () => api<Page<Campaign>>(`/workspaces/${workspaceId}/campaigns`),
    enabled: workspaceId !== '',
  });
}

export function useCampaign(workspaceId: string, campaignId: string) {
  return useQuery({
    queryKey: keys.campaign(workspaceId, campaignId),
    queryFn: () => api<Campaign>(`/workspaces/${workspaceId}/campaigns/${campaignId}`),
    enabled: workspaceId !== '' && campaignId !== '',
  });
}

export function useCreateCampaign(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; dag: { nodes: unknown[] } }) =>
      api<Campaign>(`/workspaces/${workspaceId}/campaigns`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.campaigns(workspaceId) }),
  });
}

/**
 * Set or clear a campaign's schedule. A non-null schedule with a future
 * targetStartTime hands the campaign to the scheduler, which fires it at that
 * instant (with misfire catch-up); `schedule: null` cancels a pending run.
 */
export function useScheduleCampaign(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schedule: { targetStartTime: string; state?: 'scheduled' | 'cancelled' } | null) =>
      api<Campaign>(`/workspaces/${workspaceId}/campaigns/${campaignId}`, {
        method: 'PATCH',
        body: { schedule },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.campaign(workspaceId, campaignId) });
      void qc.invalidateQueries({ queryKey: keys.campaigns(workspaceId) });
    },
  });
}

export function useCampaignExecutions(workspaceId: string, campaignId: string) {
  return useQuery({
    queryKey: keys.executions(workspaceId, campaignId),
    queryFn: () =>
      api<{ data: CampaignExecution[] }>(
        `/workspaces/${workspaceId}/campaigns/${campaignId}/executions`,
      ),
    enabled: workspaceId !== '' && campaignId !== '',
  });
}

export function useExecutionDetail(workspaceId: string, campaignId: string, executionId: string) {
  return useQuery({
    queryKey: keys.execution(workspaceId, campaignId, executionId),
    queryFn: () =>
      api<{ data: NodeExecution[] }>(
        `/workspaces/${workspaceId}/campaigns/${campaignId}/executions/${executionId}`,
      ),
    enabled: workspaceId !== '' && campaignId !== '' && executionId !== '',
    // A run in flight changes underneath the viewer.
    refetchInterval: 5_000,
  });
}

export function useExecuteCampaign(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<ExecutionSummary>(`/workspaces/${workspaceId}/campaigns/${campaignId}/execute`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.campaign(workspaceId, campaignId) });
      void qc.invalidateQueries({ queryKey: keys.executions(workspaceId, campaignId) });
    },
  });
}

/** Approve or reject a paused gate (§8.2). */
export function useDecideApproval(workspaceId: string, campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { executionId: string; nodeId: string; approved: boolean }) =>
      api<ExecutionSummary>(
        `/workspaces/${workspaceId}/campaigns/${campaignId}/nodes/${input.nodeId}/approve`,
        { method: 'POST', body: { executionId: input.executionId, approved: input.approved } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.campaign(workspaceId, campaignId) });
      void qc.invalidateQueries({ queryKey: keys.executions(workspaceId, campaignId) });
      void qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'campaign', campaignId] });
    },
  });
}

// --- Creative (§20.3) ---

export function useBrandSystem(workspaceId: string) {
  return useQuery({
    queryKey: keys.brandSystem(workspaceId),
    // The endpoint returns the whole brand_config ASSET, not the bare config —
    // the config lives under `.data`. Unwrapping here keeps the adaptation in
    // the data layer, where a contract change should be absorbed, rather than
    // leaking the envelope into every screen.
    queryFn: async () => {
      const asset = await api<Asset>(`/workspaces/${workspaceId}/brand-system`);
      return asset.data as unknown as BrandSystem;
    },
    enabled: workspaceId !== '',
    retry: false,
  });
}

export function useTemplates(workspaceId: string) {
  return useQuery({
    queryKey: keys.templates(workspaceId),
    queryFn: () => api<Page<Template>>(`/workspaces/${workspaceId}/templates`),
    enabled: workspaceId !== '',
  });
}

export function useRenderAsset(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { assetId: string; templateId: string; format?: string }) =>
      api<RenderResult>(`/workspaces/${workspaceId}/assets/${input.assetId}/render`, {
        method: 'POST',
        body: {
          templateId: input.templateId,
          output: { format: input.format ?? 'jpeg', quality: 85 },
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'assets'] }),
  });
}

/**
 * An object URL for a rendered Asset's stored bytes (§20.3).
 *
 * The preview endpoint requires the workspace authorisation header, so the URL
 * cannot go straight into an `<img src>`; the bytes are fetched through the API
 * client and wrapped in a Blob URL. The URL is revoked when the Blob changes or
 * the consumer unmounts — an unrevoked object URL pins the whole image in
 * memory for the life of the document.
 */
export function useAssetPreview(workspaceId: string, assetId: string) {
  const query = useQuery({
    queryKey: keys.assetPreview(workspaceId, assetId),
    queryFn: () => apiBlob(`/workspaces/${workspaceId}/assets/${assetId}/preview`),
    enabled: workspaceId !== '' && assetId !== '',
    retry: false,
  });

  const blob = query.data;
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (blob === undefined) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [blob]);

  return { url, isLoading: query.isLoading, error: query.error };
}

// --- Dashboard (§20.5) ---

export function useDashboard(
  workspaceId: string,
  scope: 'campaigns' | 'channels' | 'assets',
  id: string,
) {
  return useQuery({
    queryKey: keys.dashboard(workspaceId, scope, id),
    queryFn: () => api<DashboardResponse>(`/workspaces/${workspaceId}/dashboard/${scope}/${id}`),
    enabled: workspaceId !== '' && id !== '',
  });
}

/**
 * The weekly review (§20.5): per-channel leading signal and ranked performers
 * over the trailing 7 days. Read-only evidence for the human weekly cut/double
 * decision — the endpoint ranks measured performance, it does not recommend.
 */
export function useWeeklyReview(workspaceId: string) {
  return useQuery({
    queryKey: keys.weeklyReview(workspaceId),
    queryFn: () => api<WeeklyReviewResponse>(`/workspaces/${workspaceId}/analytics/weekly-review`),
    enabled: workspaceId !== '',
  });
}

/**
 * Site & audience analytics (§20.5): a data source's own measured figures — a
 * GA4 property's sessions, users, and page views over the trailing 30 days.
 * Read-only, and kept distinct from the per-channel dashboard so property-level
 * web data is never presented as a channel post's measurement (§2.7).
 */
export function useSiteAnalytics(workspaceId: string) {
  return useQuery({
    queryKey: keys.siteAnalytics(workspaceId),
    queryFn: () => api<SiteAnalyticsResponse>(`/workspaces/${workspaceId}/analytics/site`),
    enabled: workspaceId !== '',
  });
}

export function useUsage(workspaceId: string) {
  return useQuery({
    queryKey: keys.usage(workspaceId),
    queryFn: () => api<UsageResponse>(`/workspaces/${workspaceId}/usage`),
    enabled: workspaceId !== '',
  });
}

/** Start a Stripe Checkout for a tier; resolves to the hosted URL to redirect to. */
export function useStartCheckout(workspaceId: string) {
  return useMutation({
    mutationFn: (tier: 'bronze' | 'silver' | 'gold') =>
      api<{ url: string }>(`/workspaces/${workspaceId}/billing/checkout`, {
        method: 'POST',
        body: { tier },
      }),
  });
}

/** Open the Stripe Customer Portal to manage or cancel the subscription. */
export function useOpenBillingPortal(workspaceId: string) {
  return useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/workspaces/${workspaceId}/billing/portal`, { method: 'POST' }),
  });
}

export function useCollectAnalytics(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ pointsRecorded: number; destinationsWithoutMetrics: string[] }>(
        `/workspaces/${workspaceId}/analytics/collect`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ws', workspaceId, 'dashboard'] });
      qc.invalidateQueries({ queryKey: keys.siteAnalytics(workspaceId) });
    },
  });
}
